import { locales, loadExtendedTranslations } from '../locales/index.js';

/**
 * I18nService
 * 
 * Manages UI localization and translation lookups. 
 * Supports auto-detection of browser language and persistent user overrides.
 */
class I18nService {
    constructor() {
        /** @type {string} */
        this.currentLang = this.detectLanguage();
        // Lazy-load extended translations for non-English languages
        if (this.currentLang !== 'en') {
            this.ready = loadExtendedTranslations();
        } else {
            this.ready = Promise.resolve();
        }
    }

    /**
     * Resolves the target language using the following priority:
     * 1. localStorage override ('cytific_ui_lang').
     * 2. Browser navigator.language (matching the primary code, e.g., 'es-MX' -> 'es').
     * 3. Default fallback ('en').
     * 
     * @returns {string} The resolved language code.
     */
    detectLanguage() {
        // 1. Check local storage override
        const saved = localStorage.getItem('cytific_ui_lang');
        if (saved && locales[saved]) return saved;

        // 2. Check browser navigator language
        if (navigator && navigator.language) {
            const browserLang = navigator.language.split('-')[0].toLowerCase();
            if (locales[browserLang]) {
                return browserLang;
            }
        }

        // 3. Fallback to English
        return 'en';
    }

    /**
     * Sets a new UI language and persists it to localStorage.
     * Triggers a page reload to ensure the new dictionary is applied globally.
     * 
     * @param {string} lang - The new language code (e.g., 'ja').
     */
    setLanguage(lang) {
        if (locales[lang]) {
            this.currentLang = lang;
            localStorage.setItem('cytific_ui_lang', lang);
            // Reload page to apply new language across all DOM elements
            window.location.reload();
        }
    }

    getAvailableLanguages() {
        return Object.keys(locales);
    }

    getLanguageName(code) {
        const names = {
            en: 'English (US)',
            es: 'Espa\u00f1ol',
            fr: 'Fran\u00e7ais',
            de: 'Deutsch',
            ja: '\u65e5\u672c\u8a9e',
            zh: '\u7b80\u4f53\u4e2d\u6587',
            ko: '\ud55c\uad6d\uc5b4',
            pt: 'Portugu\u00eas',
            ru: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439',
            it: 'Italiano',
            tr: 'T\u00fcrk\u00e7e',
            hi: '\u0939\u093f\u0928\u094d\u0926\u0940'
        };
        return names[code] || code;
    }

    /**
     * Translates a key based on the current active dictionary.
     * 
     * @param {string} key - The dictionary key to look up.
     * @param {string} [fallback] - Optional fallback text if the key is missing in all dictionaries.
     * @returns {string} The translated string or the key itself if no match is found.
     */
    t(key, fallback) {
        // Priority: currentLang -> 'en' -> explicit fallback -> key literal
        const dictionary = locales[this.currentLang] || locales['en'];
        return dictionary[key] || locales['en'][key] || fallback || key;
    }
}

export const i18n = new I18nService();
export const t = (key, fallback) => i18n.t(key, fallback);
